import React, { useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowUpRight, Loader2, TrendingUp, Clock, DollarSign } from "lucide-react";
import useSEO from "@/lib/useSEO";
import JsonLd from "@/components/JsonLd";
import FAQAccordion from "@/components/FAQAccordion";
import { SITE_URL, breadcrumbSchema, faqPageSchema } from "@/lib/schema";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROI_FAQ = [
  {
    q: "How does the AI legal drafting ROI calculator work?",
    a: "You enter six inputs about your firm: number of attorneys, number of paralegals, blended hourly rates, weekly drafting hours per person, and the share of drafting work that is automatable. The calculator computes weekly hours saved, annual hours saved, weekly revenue value and annualized revenue value in real time.",
  },
  {
    q: "What percentage of legal drafting work is realistically automatable?",
    a: "Most law firms find that 40-60% of recurring drafting work — NDAs, MSAs, SaaS agreements, employment offers, engagement letters, discovery indexes, demand letters — is automatable with a purpose-built AI legal drafting platform. The calculator defaults to 45% as a conservative starting point.",
  },
  {
    q: "How fast is the payback period on an AI legal drafting tool?",
    a: "Most firms see net positive ROI on DraftMaster.ai inside 60-90 days at conservative use rates. Firms serving Fortune 500 corporate clients or high-volume transactional practices typically see payback in under 45 days. Solo attorneys and small firms often see it in under 30 days.",
  },
  {
    q: "Is the calculator based on real firm data?",
    a: "The defaults reflect industry-average estimates from published legal-tech studies. Adjust every input to your firm's actual figures for a personalized number. The math is transparent: hours × rate × automation share × 48 weeks.",
  },
  {
    q: "Does DraftMaster.ai serve firms outside the top US legal markets?",
    a: "Yes — DraftMaster.ai is a cloud platform serving attorneys, paralegals and corporate legal teams nationwide, including Washington DC, New York City, Los Angeles, Chicago, Houston, Atlanta and every mid-market and rural jurisdiction in between.",
  },
  {
    q: "How does this connect to the stenography side of the legal stack?",
    a: "Cleaner realtime transcripts upstream produce cleaner AI-assisted briefs downstream. Court reporters trained to certification accuracy (96%+ realtime) via StenoPaceMaster give the firms they serve a downstream advantage on every AI-drafted brief.",
  },
];

const ROI_HOWTO = {
  "@context": "https://schema.org",
  "@type": "HowTo",
  name: "How to Calculate Your Firm's AI Legal Drafting ROI",
  description:
    "Estimate the annual hours and revenue your law firm would recover by adopting an AI legal drafting platform.",
  step: [
    {
      "@type": "HowToStep",
      position: 1,
      name: "Count your legal team",
      text: "Enter the number of attorneys and paralegals whose work involves recurring drafting.",
    },
    {
      "@type": "HowToStep",
      position: 2,
      name: "Enter blended hourly rates",
      text: "Enter the blended billing or fully-loaded hourly rate for each role.",
    },
    {
      "@type": "HowToStep",
      position: 3,
      name: "Estimate weekly drafting hours",
      text: "How many hours per week does each attorney and paralegal spend on drafting-related work?",
    },
    {
      "@type": "HowToStep",
      position: 4,
      name: "Set the automatable share",
      text: "Most firms find 40-60% of recurring drafting is automatable. Adjust the slider to your firm's realistic share.",
    },
    {
      "@type": "HowToStep",
      position: 5,
      name: "Review your annualized savings",
      text: "The calculator shows weekly hours saved, annual hours saved, weekly value and annualized revenue value in real time.",
    },
  ],
};

export default function DraftingROI() {
  useSEO({
    title:
      "AI Legal Drafting ROI Calculator — Estimate Your Firm's Annual Savings | LexWorkspace",
    description:
      "Interactive ROI calculator: estimate how many hours and how much revenue your law firm would recover by adopting an AI legal drafting platform like DraftMaster.ai. Live results.",
    keywords:
      "AI legal drafting ROI, law firm automation ROI, legal AI calculator, DraftMaster ROI, contract automation savings",
    url: SITE_URL + "/tools/drafting-roi-calculator",
  });

  const [attorneys, setAttorneys] = useState(6);
  const [paralegals, setParalegals] = useState(3);
  const [attorneyRate, setAttorneyRate] = useState(450);
  const [paralegalRate, setParalegalRate] = useState(150);
  const [attorneyDraftingHours, setAttorneyDraftingHours] = useState(12); // hours/week/attorney on drafting
  const [paralegalDraftingHours, setParalegalDraftingHours] = useState(20);
  const [automatable, setAutomatable] = useState(45); // percent

  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [emailed, setEmailed] = useState(false);

  const results = useMemo(() => {
    const attHours = attorneys * attorneyDraftingHours * (automatable / 100);
    const paraHours = paralegals * paralegalDraftingHours * (automatable / 100);
    const totalWeeklyHours = attHours + paraHours;
    const totalAnnualHours = totalWeeklyHours * 48; // ~48 working weeks
    const weeklyValue = attHours * attorneyRate + paraHours * paralegalRate;
    const annualValue = weeklyValue * 48;
    const paybackWeeks = 12; // conservative estimate
    return {
      totalWeeklyHours: Math.round(totalWeeklyHours),
      totalAnnualHours: Math.round(totalAnnualHours),
      weeklyValue: Math.round(weeklyValue),
      annualValue: Math.round(annualValue),
      paybackWeeks,
    };
  }, [
    attorneys,
    paralegals,
    attorneyRate,
    paralegalRate,
    attorneyDraftingHours,
    paralegalDraftingHours,
    automatable,
  ]);

  const money = (n) =>
    "$" + n.toLocaleString("en-US", { maximumFractionDigits: 0 });

  const submitEmail = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) {
      toast.error("Please enter a valid email");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        email,
        interest: "draftmaster",
        source: "roi-calculator",
      });
      setEmailed(true);
      toast.success("Detailed report request received — check your inbox.");
      if (window.gtag) {
        window.gtag("event", "roi_lead_submit", {
          annual_value: results.annualValue,
        });
      }
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="roi-page">
      <JsonLd id="roi-faq" data={faqPageSchema(ROI_FAQ)} />
      <JsonLd id="roi-howto" data={ROI_HOWTO} />
      <JsonLd
        id="roi-breadcrumbs"
        data={breadcrumbSchema([
          { name: "Home", url: SITE_URL + "/" },
          { name: "Tools", url: SITE_URL + "/tools" },
          {
            name: "AI Drafting ROI Calculator",
            url: SITE_URL + "/tools/drafting-roi-calculator",
          },
        ])}
      />
      <JsonLd
        id="roi-app"
        data={{
          "@context": "https://schema.org",
          "@type": "WebApplication",
          name: "AI Legal Drafting ROI Calculator",
          url: SITE_URL + "/tools/drafting-roi-calculator",
          applicationCategory: "BusinessApplication",
          browserRequirements: "Requires JavaScript",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
        }}
      />

      {/* HERO */}
      <section className="border-b border-black/10">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 py-20 lg:py-28">
          <p className="text-xs tracking-[0.3em] uppercase text-gray-500 mb-4">
            Interactive Tool
          </p>
          <h1
            data-testid="roi-h1"
            className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] max-w-3xl"
          >
            AI Legal Drafting{" "}
            <span className="text-[#065F46]">ROI Calculator</span>
          </h1>
          <p className="text-gray-600 mt-6 max-w-2xl leading-relaxed">
            Answer six inputs about your firm. See — live — how many hours and
            how much annual revenue an AI legal drafting platform would recover
            across your team.
          </p>
        </div>
      </section>

      {/* CALCULATOR */}
      <section className="py-16 lg:py-24">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* INPUTS */}
          <div className="border border-black/10 p-8 lg:p-10 bg-white">
            <p className="text-xs tracking-[0.3em] uppercase text-gray-500 mb-8">
              Your firm
            </p>
            <div className="space-y-8">
              <NumberField
                label="Attorneys"
                value={attorneys}
                onChange={setAttorneys}
                min={1}
                max={500}
                testId="roi-attorneys"
              />
              <NumberField
                label="Paralegals"
                value={paralegals}
                onChange={setParalegals}
                min={0}
                max={500}
                testId="roi-paralegals"
              />
              <NumberField
                label="Blended attorney rate ($/hr)"
                value={attorneyRate}
                onChange={setAttorneyRate}
                min={100}
                max={2500}
                step={25}
                testId="roi-attorney-rate"
              />
              <NumberField
                label="Blended paralegal rate ($/hr)"
                value={paralegalRate}
                onChange={setParalegalRate}
                min={50}
                max={500}
                step={10}
                testId="roi-paralegal-rate"
              />
              <NumberField
                label="Drafting hours / week / attorney"
                value={attorneyDraftingHours}
                onChange={setAttorneyDraftingHours}
                min={1}
                max={40}
                testId="roi-attorney-hours"
              />
              <NumberField
                label="Drafting hours / week / paralegal"
                value={paralegalDraftingHours}
                onChange={setParalegalDraftingHours}
                min={1}
                max={40}
                testId="roi-paralegal-hours"
              />
              <SliderField
                label="Automatable share of drafting work"
                value={automatable}
                onChange={setAutomatable}
                min={20}
                max={80}
                unit="%"
                testId="roi-automatable"
              />
            </div>
            <p className="text-xs text-gray-500 mt-8 leading-relaxed">
              Defaults reflect industry-average estimates. Adjust to your firm's
              actual figures for a personalized number.
            </p>
          </div>

          {/* RESULTS */}
          <div className="border border-black/10 p-8 lg:p-10 bg-[#ECFDF5]">
            <p className="text-xs tracking-[0.3em] uppercase text-[#065F46] font-semibold mb-8">
              Your annualized savings
            </p>

            <motion.div
              key={results.annualValue}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="mb-10"
            >
              <p
                data-testid="roi-annual-value"
                className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-none text-[#065F46]"
              >
                {money(results.annualValue)}
              </p>
              <p className="text-sm text-gray-600 mt-3">
                Estimated annualized revenue recovered by automating{" "}
                {automatable}% of your drafting workload.
              </p>
            </motion.div>

            <div className="grid grid-cols-2 gap-6 mb-10">
              <ResultStat
                icon={Clock}
                label="Hours/week saved"
                value={results.totalWeeklyHours.toString()}
                testId="roi-weekly-hours"
              />
              <ResultStat
                icon={TrendingUp}
                label="Hours/year saved"
                value={results.totalAnnualHours.toLocaleString()}
                testId="roi-annual-hours"
              />
              <ResultStat
                icon={DollarSign}
                label="Weekly value"
                value={money(results.weeklyValue)}
                testId="roi-weekly-value"
              />
              <ResultStat
                icon={TrendingUp}
                label="Typical payback"
                value={`~${results.paybackWeeks} wks`}
                testId="roi-payback"
              />
            </div>

            {/* Email capture for detailed report */}
            {!emailed ? (
              <form onSubmit={submitEmail} className="space-y-4" data-testid="roi-email-form">
                <label className="block text-xs tracking-widest uppercase text-gray-500">
                  Get a detailed PDF report emailed to you
                </label>
                <div className="flex flex-col sm:flex-row gap-3">
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@firm.com"
                    data-testid="roi-email-input"
                    className="flex-1 border border-black/20 bg-white px-4 py-3 text-sm focus:border-black focus:outline-none"
                  />
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="roi-email-submit"
                    className="btn-sharp bg-[#065F46] text-white hover:bg-[#047857] inline-flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {submitting && <Loader2 size={14} className="animate-spin" />}
                    Send report
                  </button>
                </div>
              </form>
            ) : (
              <div
                data-testid="roi-email-success"
                className="border-l-4 border-[#065F46] bg-white p-5"
              >
                <p className="font-serif-display text-xl mb-1">
                  Detailed report is on its way.
                </p>
                <p className="text-sm text-gray-600">
                  Check your inbox in the next few minutes.
                </p>
              </div>
            )}

            <a
              href="https://draftmaster.ai"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="roi-cta-draft"
              className="btn-sharp bg-black text-white hover:bg-gray-900 mt-6 inline-flex items-center gap-2"
            >
              Open draftmaster.ai <ArrowUpRight size={14} />
            </a>
          </div>
        </div>
      </section>

      {/* CROSS PROMO */}
      <section className="border-t border-black/10 py-16 lg:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 grid grid-cols-1 md:grid-cols-2 gap-0 border border-black/10">
          <a
            href="https://draftmaster.ai"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="roi-footer-cta-draft"
            className="p-8 lg:p-10 border-b md:border-b-0 md:border-r border-black/10 hover:bg-[#ECFDF5] transition-colors group"
            style={{ borderLeftWidth: 4, borderLeftColor: "#065F46" }}
          >
            <p className="text-xs tracking-[0.25em] uppercase font-semibold text-[#065F46] mb-3">
              For attorneys & legal teams
            </p>
            <p className="font-serif-display text-2xl leading-tight mb-3">
              Capture the savings on DraftMaster.ai.
            </p>
            <p className="text-sm text-gray-600 leading-relaxed mb-6">
              AI legal drafting purpose-built for law firms — clause-library
              control, citation safety, per-clause audit trails.
            </p>
            <span className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-semibold text-[#065F46] group-hover:gap-3 transition-all">
              Visit draftmaster.ai <ArrowUpRight size={14} />
            </span>
          </a>

          <a
            href="https://stenopacemaster.com"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="roi-footer-cta-steno"
            className="p-8 lg:p-10 hover:bg-[#EFF6FF] transition-colors group"
            style={{ borderLeftWidth: 4, borderLeftColor: "#1E3A8A" }}
          >
            <p className="text-xs tracking-[0.25em] uppercase font-semibold text-[#1E3A8A] mb-3">
              For the reporters serving your firm
            </p>
            <p className="font-serif-display text-2xl leading-tight mb-3">
              Cleaner transcripts on StenoPaceMaster.
            </p>
            <p className="text-sm text-gray-600 leading-relaxed mb-6">
              Cleaner realtime transcripts upstream produce cleaner AI-assisted
              briefs downstream. Train reporters to 96%+ certification accuracy.
            </p>
            <span className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-semibold text-[#1E3A8A] group-hover:gap-3 transition-all">
              Visit stenopacemaster.com <ArrowUpRight size={14} />
            </span>
          </a>
        </div>
      </section>
      {/* GEO + FAQ */}
      <section className="border-t border-black/10 py-16 lg:py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <p className="text-xs tracking-[0.3em] uppercase text-gray-500 mb-4">
              Frequently asked
            </p>
            <h2 className="font-serif-display text-3xl sm:text-4xl leading-tight mb-4">
              About the ROI calculator.
            </h2>
            <p className="text-sm text-gray-600 leading-relaxed">
              Used by attorneys, paralegals and legal-ops teams in Washington
              DC, New York City, Los Angeles, Chicago, Houston, Atlanta and
              across the US.
            </p>
          </div>
          <div className="lg:col-span-8">
            <FAQAccordion items={ROI_FAQ} testIdPrefix="roi-faq" />
          </div>
        </div>
      </section>
    </div>
  );
}

function NumberField({ label, value, onChange, min, max, step = 1, testId }) {
  return (
    <div>
      <label className="block text-xs tracking-widest uppercase text-gray-500 mb-2">
        {label}
      </label>
      <input
        type="number"
        value={value}
        min={min}
        max={max}
        step={step}
        data-testid={testId}
        onChange={(e) => {
          const v = Number(e.target.value);
          if (Number.isFinite(v)) onChange(Math.min(max, Math.max(min, v)));
        }}
        className="w-full border-b border-black/20 bg-transparent rounded-none px-0 py-2 focus:border-black focus:outline-none font-serif-display text-2xl"
      />
    </div>
  );
}

function SliderField({ label, value, onChange, min, max, unit = "", testId }) {
  return (
    <div>
      <div className="flex justify-between items-baseline mb-2">
        <label className="text-xs tracking-widest uppercase text-gray-500">
          {label}
        </label>
        <span className="font-serif-display text-2xl">
          {value}
          {unit}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        data-testid={testId}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-[#065F46]"
      />
    </div>
  );
}

function ResultStat({ icon: Icon, label, value, testId }) {
  return (
    <div className="bg-white border border-black/10 p-4" data-testid={testId}>
      <Icon size={16} className="text-[#065F46] mb-2" />
      <p className="text-xs tracking-widest uppercase text-gray-500 mb-1">
        {label}
      </p>
      <p className="font-serif-display text-2xl leading-none">{value}</p>
    </div>
  );
}
